import {
  WSButton,
  WSElement,
  WSEmptyState,
  WSFlexBox,
  WSSectionToolbar
} from "@wingspanhq/fe-component-library";
import {
  PayableRow,
  SearchInvoiceRowsSortParameter,
  SearchPayableRowsFilterParameter
} from "@wingspanhq/wingspan-api-sdk";
import { queriesPayableRows } from "@query";
import React, { useMemo } from "react";
import { ButtonDownloadCsv } from "../../../../shared/components/ButtonDownloadCsv";
import { ClientPayablesTable } from "./ClientPayablesTable";
import { getClientPayablesCsvData } from "./useDownloadClientPayablesCsv";

const PAGE_SIZE = 10;

type Props = {
  title: string;
  filter: SearchPayableRowsFilterParameter;
  sort: SearchInvoiceRowsSortParameter;
  dateField: "due" | "paid";
  csvFileName: string;
  onRowClick: (payable: PayableRow) => void;
  // Reports whether this section has anything to show once it settles, so
  // the parent can decide whether to fall back to a single "no invoices"
  // empty state across all sections.
  onSettled?: (hasItems: boolean) => void;
};

export const ClientPayablesSection: React.FC<Props> = props => {
  const request = useMemo(
    () => ({
      filter: props.filter,
      sort: props.sort,
      page: { size: PAGE_SIZE }
    }),
    [props.filter, props.sort]
  );

  const query = queriesPayableRows.searchPayableRows.usePaginatedQuery(request);
  const items = query.flatData ?? [];

  React.useEffect(() => {
    if (!query.isLoading) {
      props.onSettled?.(items.length > 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isLoading, items.length]);

  if (query.isLoading) {
    return (
      <WSElement>
        <WSSectionToolbar title={props.title} />
        <ClientPayablesTable loading items={[]} dateField={props.dateField} />
      </WSElement>
    );
  }

  if (query.isError) {
    return (
      <WSElement>
        <WSSectionToolbar title={props.title} />
        <WSEmptyState
          type="error"
          title="Something went wrong"
          description="We couldn't load these invoices."
          actions={{
            buttons: [{ label: "Try again", onClick: () => query.refetch() }]
          }}
        />
      </WSElement>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <WSElement>
      <WSFlexBox wrap="nowrap" justify="space-between" alignItems="center">
        <WSSectionToolbar title={props.title} />
        <ButtonDownloadCsv
          getData={() => getClientPayablesCsvData(props.filter)}
          fileName={props.csvFileName}
        />
      </WSFlexBox>

      <ClientPayablesTable
        items={items}
        dateField={props.dateField}
        onRowClick={props.onRowClick}
      />

      {query.hasNextPage && (
        <WSButton.Link
          mt="M"
          fullWidth
          onClick={() => {
            query.fetchNextPage();
          }}
          loading={!!query.isFetchingNextPage}
        >
          Load more
        </WSButton.Link>
      )}
    </WSElement>
  );
};
